import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2, Heart, Weight, Ruler, Moon, Footprints, Flame } from "lucide-react";
import { ScreenHeader, Card } from "./MobileShell";
import { clientLogs } from "../lib/api";
import LineChart from "../components/LineChart";

export default function ProgressScreen() {
  const { token } = useOutletContext();
  const [logs, setLogs] = useState({ daily: [], weekly: [], monthly: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const l = await clientLogs(token, 60);
        if (active)
          setLogs({ daily: l.daily || [], weekly: l.weekly || [], monthly: l.monthly || [] });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const latest = useMemo(() => {
    const dailyToday = logs.daily[0] || {};
    const latestWeekly = logs.weekly[0] || {};
    const latestMonthly = logs.monthly[0] || {};
    return { dailyToday, latestWeekly, latestMonthly };
  }, [logs]);

  const weightSeries = useMemo(
    () =>
      [...logs.weekly]
        .reverse()
        .filter((w) => w.weight_kg != null)
        .map((w) => ({ x: w.week_start, y: w.weight_kg })),
    [logs.weekly]
  );
  const stepsSeries = useMemo(
    () =>
      [...logs.daily]
        .reverse()
        .filter((d) => d.steps != null)
        .map((d) => ({ x: d.log_date, y: d.steps })),
    [logs.daily]
  );
  const sleepSeries = useMemo(
    () =>
      [...logs.daily]
        .reverse()
        .filter((d) => d.sleep_h != null)
        .map((d) => ({ x: d.log_date, y: d.sleep_h })),
    [logs.daily]
  );

  if (loading) {
    return (
      <div className="min-h-[50vh] grid place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-muted)]" />
      </div>
    );
  }

  const workoutsLast7 = logs.daily.slice(0, 7).filter((d) => d.workout_done).length;
  const foodDaysLast7 = logs.daily.slice(0, 7).filter((d) => (d.food || []).length).length;

  return (
    <>
      <ScreenHeader eyebrow="Progress" title="Where you stand" />

      <div className="grid grid-cols-2 gap-3 mb-4" data-testid="progress-kpis">
        <KPI
          icon={Weight}
          label="Weight"
          value={latest.latestWeekly.weight_kg ? `${latest.latestWeekly.weight_kg} kg` : "—"}
          hint={latest.latestWeekly.week_start}
        />
        <KPI
          icon={Heart}
          label="Resting HR"
          value={latest.latestWeekly.resting_hr ? `${latest.latestWeekly.resting_hr}` : "—"}
          hint="bpm · weekly"
        />
        <KPI
          icon={Ruler}
          label="Waist"
          value={latest.latestMonthly.waist_cm ? `${latest.latestMonthly.waist_cm} cm` : "—"}
          hint="monthly"
        />
        <KPI
          icon={Flame}
          label="Blood pressure"
          value={
            latest.latestWeekly.bp_sys && latest.latestWeekly.bp_dia
              ? `${latest.latestWeekly.bp_sys}/${latest.latestWeekly.bp_dia}`
              : "—"
          }
          hint="mmHg · weekly"
        />
      </div>

      {/* Weight trend */}
      <Card className="mb-4" data-testid="progress-weight-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="eyebrow text-[var(--lc-muted)]">Weight trend</div>
            <div className="font-serif-display text-lg mt-1">Last {weightSeries.length} weeks</div>
          </div>
        </div>
        {weightSeries.length >= 2 ? (
          <LineChart data={weightSeries} height={120} suffix=" kg" />
        ) : (
          <Empty label="Log your weight weekly in the tracker to see the trend here." />
        )}
      </Card>

      {/* Steps trend */}
      <Card className="mb-4" data-testid="progress-steps-card">
        <div className="eyebrow text-[var(--lc-muted)] mb-1">Daily steps</div>
        {stepsSeries.length >= 2 ? (
          <LineChart data={stepsSeries} height={110} />
        ) : (
          <Empty label="Walking data will show here once your coach starts you tracking steps." />
        )}
      </Card>

      {/* Sleep trend */}
      <Card className="mb-4" data-testid="progress-sleep-card">
        <div className="flex items-center gap-2 mb-1">
          <Moon className="h-3.5 w-3.5 text-[var(--lc-moss-dark)]" />
          <div className="eyebrow text-[var(--lc-muted)]">Sleep hours</div>
        </div>
        {sleepSeries.length >= 2 ? (
          <LineChart data={sleepSeries} height={110} suffix=" h" />
        ) : (
          <Empty label="Add sleep hours daily to see your pattern." />
        )}
      </Card>

      {/* Adherence this week */}
      <Card className="mb-4" data-testid="progress-adherence-card">
        <div className="eyebrow text-[var(--lc-muted)] mb-3">This week</div>
        <div className="grid grid-cols-2 gap-3">
          <Mini label="Workouts done" value={`${workoutsLast7}/7`} icon={Footprints} />
          <Mini label="Days with food log" value={`${foodDaysLast7}/7`} icon={Flame} />
        </div>
      </Card>

      <Card data-testid="progress-goals-card">
        <div className="eyebrow text-[var(--lc-muted)]">Personal goals</div>
        <p className="text-sm text-[var(--lc-ink)] mt-2 leading-relaxed">
          Your coach reviews goals monthly. Open the full tracker to add weight,
          grip strength, lab-ready vitals, and more.
        </p>
        <a
          href="/portal"
          className="mt-3 inline-flex items-center gap-1 text-[11px] eyebrow text-[var(--lc-moss-dark)]"
        >
          Open daily tracker →
        </a>
      </Card>
    </>
  );
}

function KPI({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-[var(--lc-line)] rounded-xl p-3">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-[var(--lc-moss-dark)]" />
        <span className="eyebrow text-[var(--lc-muted)]">{label}</span>
      </div>
      <div className="font-serif-display text-xl mt-1.5 leading-tight">{value}</div>
      {hint && <div className="text-[10px] text-[var(--lc-muted)] mt-0.5">{hint}</div>}
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="bg-[var(--lc-cream)] border border-[var(--lc-line)] rounded-md p-3">
      <div className="flex items-center gap-1.5 eyebrow text-[var(--lc-muted)]">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="font-serif-display text-xl mt-1.5">{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="py-6 text-center">
      <div className="text-xs text-[var(--lc-muted)]">{label}</div>
    </div>
  );
}
